import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { DeleteOutline, AddCircleOutline } from "@mui/icons-material";
import useLoader from "../../../../hooks/useLoader.js";
import useModal from "../../../../hooks/useModal.js";
import { createMinimumWages, updateMinimumWages, getMinimumWagesList, getZones } from "../../../../services/minimumWages.js";
import { MW_DETAIL_KEYS, MW_EFFECTIVE_FROM_KEY, MW_EFFECTIVE_TO_KEY, getDetailValue, hasDetailKey } from "./column.js";

// details[].key values (e.g. "Semi-skilled", "Highly Skilled") aren't valid form field
// names, so each row's formData uses these safe keys and maps back to the backend key
// on submit/populate.
const DETAIL_FIELD_MAP = {
  Unskilled: "unskilled",
  "Semi-skilled": "semiSkilled",
  Skilled: "skilled",
  "Highly Skilled": "highlySkilled",
};

const STATUS_OPTIONS = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];

const INITIAL_FORM = {
  state: "",
  unskilled: "",
  semiSkilled: "",
  skilled: "",
  highlySkilled: "",
  effective_from: "",
  effective_to: "",
  status: 1,
};

// Backend validation errors (zod) are keyed by the backend field name, which differs from
// the frontend form field name only for state (state_id -> state).
const SERVER_FIELD_MAP = { state_id: "state" };

const isBlank = (val) => val === "" || val === null || val === undefined;

// Pure validation for a single row's fields. usedStates is the set of states that already
// have a MW record in the DB (excluding this record itself on edit); siblingStates is the
// set of states already picked by other rows open in the same modal. Backend validation
// (ONE STATE = ONE MW RECORD) remains the source of truth - this is only for faster UX.
function computeFieldErrors(formData, { usedStates, siblingStates }) {
  const errors = {};

  if (!formData.state) {
    errors.state = "State is required";
  } else {
    const key = String(formData.state);
    if (usedStates && usedStates.has(key)) {
      errors.state = "Minimum Wages already exists for this state";
    } else if (siblingStates && siblingStates.has(key)) {
      errors.state = "This state is already used in another row";
    }
  }

  MW_DETAIL_KEYS.forEach((detailKey) => {
    const field = DETAIL_FIELD_MAP[detailKey];
    const val = formData[field];
    const num = Number(val);

    if (isBlank(val)) {
      errors[field] = `${detailKey} is required`;
    } else if (Number.isNaN(num)) {
      errors[field] = `${detailKey} must be a valid number`;
    } else if (num < 0) {
      errors[field] = `${detailKey} must be greater than or equal to 0`;
    }
  });

  // Effective From is required; Effective To is an optional month-year value. When both are
  // present, Effective To must not be earlier than Effective From (plain YYYY-MM string
  // comparison works here).
  if (isBlank(formData.effective_from)) {
    errors.effective_from = "Effective From is required";
  } else if (!isBlank(formData.effective_to) && formData.effective_to < formData.effective_from) {
    errors.effective_to = "Effective To must be on or after Effective From";
  }

  return errors;
}

function getSiblingStates(rowsSnapshot, index) {
  const set = new Set();
  rowsSnapshot.forEach((r, i) => {
    if (i !== index && r.formData.state) set.add(String(r.formData.state));
  });
  return set;
}

function recomputeAllRows(rowsSnapshot, usedStates) {
  return rowsSnapshot.map((row, index) => {
    if (row.saved) return row;
    const siblingStates = getSiblingStates(rowsSnapshot, index);
    const errors = computeFieldErrors(row.formData, { usedStates, siblingStates });
    return { ...row, errors };
  });
}

function extractServerFieldErrors(respData) {
  const issues = Array.isArray(respData?.data) ? respData.data : null;
  if (!issues || !issues.length) return {};

  const fieldErrors = {};
  issues.forEach((issue) => {
    const backendField = issue?.path?.[0];
    if (!backendField) return;
    const field = SERVER_FIELD_MAP[backendField] || backendField;
    fieldErrors[field] = issue.message;
  });
  return fieldErrors;
}

// Centralized details-JSON builder, shared by Add and Edit so the two never drift apart.
// Effective From is required and always included; Effective To is only included when a
// non-blank value was provided - an empty Effective To is never stored as a JSON entry
// (no "Effective To": "" / null), and clearing it during Edit simply drops it from the array.
function buildDetails(formData) {
  const details = MW_DETAIL_KEYS.map((key) => ({
    key,
    value: Number(formData[DETAIL_FIELD_MAP[key]]),
  }));

  details.push({ key: MW_EFFECTIVE_FROM_KEY, value: formData.effective_from });

  if (!isBlank(formData.effective_to)) {
    details.push({ key: MW_EFFECTIVE_TO_KEY, value: formData.effective_to });
  }

  return details;
}

function buildPayload(formData) {
  const payload = {
    state_id: Number(formData.state),
    details: buildDetails(formData),
    status: Number(formData.status),
  };

  // effective_from/effective_to are also sent as top-level fields to populate the separate
  // pms_compliance.effective_from/effective_to DATEONLY columns (the backend converts the
  // raw month to the 1st/last day of that month) - the same raw month values are the ones
  // stored inside `details` above, which is the source of truth for Add/Edit/View.
  payload.effective_from = formData.effective_from;
  payload.effective_to = isBlank(formData.effective_to) ? null : formData.effective_to;

  return payload;
}

const MinimumWagesRowForm = ({ row, index, zones, isEdit, canRemove, isSubmitting, onFieldChange, onRemove }) => {
  const { formData, errors, saved } = row;

  const handleChange = (e) => {
    const { name, value } = e.target;
    onFieldChange(index, name, name === "status" ? Number(value) : value);
  };

  const disabled = saved || isSubmitting;

  return (
    <div
      style={{
        backgroundColor: "#f9f9f9",
        padding: "14px",
        borderRadius: "6px",
        border: saved ? "1px solid #28a745" : "1px solid #e0e0e0",
        marginBottom: "14px",
      }}
    >
      {!isEdit && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong style={{ fontSize: "13px" }}>Row {index + 1}</strong>
          {canRemove && !saved && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={isSubmitting}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#dc3545", padding: 0 }}
              title="Remove row"
            >
              <DeleteOutline fontSize="small" />
            </button>
          )}
        </div>
      )}

      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            State <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select form-select-sm ${errors.state ? "is-invalid" : ""}`}
            name="state"
            value={formData.state}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="">Select State</option>
            {zones.map((zone) => (
              <option key={zone._ZoneID} value={zone._ZoneID}>
                {zone._Name}
              </option>
            ))}
          </select>
          {errors.state && <div className="invalid-feedback">{errors.state}</div>}
        </div>

        {MW_DETAIL_KEYS.map((detailKey) => {
          const field = DETAIL_FIELD_MAP[detailKey];
          return (
            <div className="col-md-4 mb-3" key={field}>
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                {detailKey} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                name={field}
                className={`form-control form-control-sm ${errors[field] ? "is-invalid" : ""}`}
                value={formData[field]}
                onChange={handleChange}
                placeholder={`Enter ${detailKey.toLowerCase()} amount`}
                disabled={disabled}
              />
              {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
            </div>
          );
        })}

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Effective From <span className="text-danger">*</span>
          </label>
          <div className="d-flex align-items-center gap-1">
            <input
              type="month"
              name="effective_from"
              className={`form-control form-control-sm ${errors.effective_from ? "is-invalid" : ""}`}
              value={formData.effective_from}
              onChange={handleChange}
              placeholder="Select month"
              disabled={disabled}
            />
          </div>
          {errors.effective_from && <div className="invalid-feedback d-block">{errors.effective_from}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Effective To
          </label>
          <div className="d-flex align-items-center gap-1">
            <input
              type="month"
              name="effective_to"
              className={`form-control form-control-sm ${errors.effective_to ? "is-invalid" : ""}`}
              value={formData.effective_to}
              onChange={handleChange}
              placeholder="Select month (optional)"
              min={formData.effective_from || undefined}
              disabled={disabled}
            />
            {!!formData.effective_to && !disabled && (
              <button
                type="button"
                onClick={() => onFieldChange(index, "effective_to", "")}
                title="Clear"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: "16px", lineHeight: 1, padding: "0 4px" }}
              >
                &times;
              </button>
            )}
          </div>
          {errors.effective_to && <div className="invalid-feedback d-block">{errors.effective_to}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Status
          </label>
          <select className="form-select form-select-sm" name="status" value={formData.status} onChange={handleChange} disabled={disabled}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isEdit && saved && (
        <div className="d-flex justify-content-end align-items-center" style={{ paddingTop: "6px" }}>
          <span className="text-success" style={{ fontSize: "12px", fontWeight: 600 }}>
            Saved successfully
          </span>
        </div>
      )}
    </div>
  );
};

const AddEditMinimumWages = ({ refresh, existingField = null }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const isEdit = Boolean(existingField);
  const excludeId = isEdit ? existingField.id : undefined;

  const [zones, setZones] = useState([]);
  const [usedStates, setUsedStates] = useState(new Set());
  const usedStatesRef = useRef(new Set());

  const rowCounterRef = useRef(0);
  const makeEmptyRow = () => ({
    key: ++rowCounterRef.current,
    formData: { ...INITIAL_FORM },
    errors: {},
    saved: false,
  });

  const [rows, setRows] = useState(() => (isEdit ? [] : [makeEmptyRow()]));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Errors stay hidden while the user is filling the form in and only appear once they've
  // clicked Submit/Update at least once; after that first attempt, edits re-validate live so
  // fixed fields clear immediately instead of requiring another submit click. Mirrors the same
  // pattern used in AddEditPTMaster.jsx / AddEditLWFMaster.jsx.
  const attemptedSubmitRef = useRef(false);
  const applyRows = useCallback(
    (nextRows) => (attemptedSubmitRef.current ? recomputeAllRows(nextRows, usedStatesRef.current) : nextRows),
    []
  );

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const response = await getZones();
        const zoneData = response?.data?.data || response?.data || [];
        setZones(zoneData);
      } catch (error) {
        console.error("Error fetching zones:", error);
        toast.error("Failed to load states");
      }
    };
    fetchZones();
  }, []);

  const loadUsedStates = useCallback(async () => {
    try {
      const response = await getMinimumWagesList(1, { limit: 1000 });
      const body = response?.data;
      const listRows = body?.data?.data || body?.data || [];
      const states = new Set(
        listRows.filter((r) => !excludeId || r.id !== excludeId).map((r) => String(r.state_id))
      );
      usedStatesRef.current = states;
      setUsedStates(states);
      if (attemptedSubmitRef.current) {
        setRows((prev) => recomputeAllRows(prev, states));
      }
    } catch (error) {
      console.error("Error fetching existing Minimum Wages states:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeId]);

  useEffect(() => {
    loadUsedStates();
  }, [loadUsedStates]);

  useEffect(() => {
    if (isEdit && existingField) {
      const details = existingField.details;

      // Effective From/To are read from the `details` JSON via an existence check - not
      // assumed present - so a record where Effective To was never set (or a legacy record
      // saved before these keys existed in `details`) populates as empty instead of erroring.
      // The separate effective_from/effective_to DB columns are only a fallback for legacy
      // records that predate this JSON representation.
      const effectiveFrom = hasDetailKey(details, MW_EFFECTIVE_FROM_KEY)
        ? getDetailValue(details, MW_EFFECTIVE_FROM_KEY) ?? ""
        : existingField.effective_from
        ? String(existingField.effective_from).slice(0, 7)
        : "";

      const effectiveTo = hasDetailKey(details, MW_EFFECTIVE_TO_KEY)
        ? getDetailValue(details, MW_EFFECTIVE_TO_KEY) ?? ""
        : existingField.effective_to
        ? String(existingField.effective_to).slice(0, 7)
        : "";

      const formData = {
        state: existingField.state_id ?? existingField.state ?? "",
        unskilled: getDetailValue(details, "Unskilled") ?? "",
        semiSkilled: getDetailValue(details, "Semi-skilled") ?? "",
        skilled: getDetailValue(details, "Skilled") ?? "",
        highlySkilled: getDetailValue(details, "Highly Skilled") ?? "",
        effective_from: effectiveFrom,
        effective_to: effectiveTo,
        status: existingField.status === undefined || existingField.status === null ? 1 : Number(existingField.status),
      };
      setRows([{ key: 1, formData, errors: {}, saved: false }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingField]);

  const handleFieldChange = (index, name, value) => {
    setRows((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, formData: { ...row.formData, [name]: value } } : row));
      return applyRows(next);
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => applyRows(prev.filter((_, i) => i !== index)));
  };

  const handleSubmitAll = async () => {
    if (isSubmitting) return;

    attemptedSubmitRef.current = true;
    const validatedRows = recomputeAllRows(rows, usedStatesRef.current);
    const hasErrors = validatedRows.some((row) => !row.saved && Object.keys(row.errors).length > 0);

    if (hasErrors) {
      setRows(validatedRows);
      return;
    }

    setRows(validatedRows);
    setIsSubmitting(true);
    showLoader();

    try {
      let latestRows = validatedRows;
      let successMessage = "";
      let sawFailure = false;

      for (let index = 0; index < latestRows.length; index += 1) {
        const row = latestRows[index];
        if (row.saved) continue;

        const payload = buildPayload(row.formData);

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = isEdit ? await updateMinimumWages(existingField.id, payload) : await createMinimumWages(payload);

          successMessage = response?.data?.message || successMessage;
          latestRows = latestRows.map((r, i) => (i === index ? { ...r, saved: true, errors: {} } : r));
          setRows(latestRows);

          if (!isEdit) {
            await loadUsedStates();
          }
        } catch (error) {
          console.error("Error saving Minimum Wages:", error);
          sawFailure = true;
          const respData = error?.response?.data;
          const serverFieldErrors = extractServerFieldErrors(respData);
          const message = respData?.message || "Failed to save Minimum Wages";
          const hasFieldErrors = Object.keys(serverFieldErrors).length > 0;

          latestRows = latestRows.map((r, i) => (i === index ? { ...r, errors: { ...r.errors, ...serverFieldErrors } } : r));
          setRows(latestRows);
          if (!hasFieldErrors) {
            // Business/overlap validation errors (e.g. state-already-used) have no single
            // field to attach to, so they're surfaced via toast only - never rendered inline.
            toast.error(message);
          }
        }
      }

      if (!sawFailure) {
        toast.success(successMessage || (isEdit ? "Minimum Wages record updated successfully!" : "Minimum Wages record added successfully!"));
        refresh?.();
        hideModal?.();
      }
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "4px" }}>
        {rows.map((row, index) => (
          <MinimumWagesRowForm
            key={row.key}
            row={row}
            index={index}
            zones={zones}
            isEdit={isEdit}
            canRemove={rows.length > 1}
            isSubmitting={isSubmitting}
            onFieldChange={handleFieldChange}
            onRemove={handleRemoveRow}
          />
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "12px",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <div>
          {!isEdit && (
            <button
              type="button"
              onClick={handleAddRow}
              disabled={isSubmitting}
              style={{
                backgroundColor: "#f0f0f0",
                border: "1px dashed #999",
                padding: "5px 10px",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "#333",
              }}
            >
              <AddCircleOutline fontSize="small" />
              Add Row
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isSubmitting}>
            {isEdit ? "Cancel" : "Close"}
          </button>
          <button type="button" className="btn btn-success btn-sm" disabled={isSubmitting} onClick={handleSubmitAll}>
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditMinimumWages;
